import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectEmployee, selectUser, selectUserRoles } from "../store/store-selector";
import { clearUser } from "../store/slice/user-slice";
import { UserRoles } from "../AppConstants";

export function withEmployeeRequired(Component) {
  return function ProtectedEmployeeRoute() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const loggedInEmployee = useSelector(selectEmployee);
    useEffect(() => {
      if (!loggedInEmployee) {
        dispatch(clearUser());
        navigate("/");
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loggedInEmployee]);

    return loggedInEmployee && <Component />;
  };
}

export function withUserRequired(Component) {
  return function ProtectedUserRoute() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const authUser = useSelector(selectUser);
    useEffect(() => {
      if (!authUser) {
        dispatch(clearUser());
        navigate("/");
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authUser]);

    return authUser && <Component />;
  };
}

export function withUserRoleRequired(Component, props) {
  return function ProtectedUserRoleRoute() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const loggedInEmployee = useSelector(selectEmployee);
    const loggedInUserRoles = useSelector(selectUserRoles);
    useEffect(() => {
      if (
        !(
          loggedInEmployee &&
          loggedInUserRoles &&
          loggedInUserRoles.length &&
          loggedInUserRoles.some(
            (item) =>
              item.UserRoleCode === UserRoles.PricingAdmin ||
              loggedInUserRoles.some((item) => item.UserRoleCode === UserRoles.Viewer)
          )
        )
      ) {
        if (loggedInEmployee) navigate("/home");
        else {
          dispatch(clearUser());
          navigate("/");
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loggedInEmployee]);

    return loggedInEmployee && <Component {...props} />;
  };
}

export function withSuperAdminRoleRequired(Component) {
  return function ProtectedUserRoleRoute() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const loggedInEmployee = useSelector(selectEmployee);
    const loggedInUserRoles = useSelector(selectUserRoles);
    useEffect(() => {
      if (
        !(
          loggedInEmployee &&
          loggedInUserRoles &&
          loggedInUserRoles.length &&
          loggedInUserRoles.some((item) => item.UserRoleCode === UserRoles.SuperAdmin)
        )
      ) {
        if (loggedInEmployee) navigate("/home");
        else {
          dispatch(clearUser());
          navigate("/");
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loggedInEmployee]);

    return loggedInEmployee && <Component />;
  };
}

export function withPricingApproverRoleRequired(Component) {
  return function ProtectedUserRoleRoute() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const loggedInEmployee = useSelector(selectEmployee);
    const loggedInUserRoles = useSelector(selectUserRoles);
    useEffect(() => {
      if (
        !(
          loggedInEmployee &&
          loggedInUserRoles &&
          loggedInUserRoles.length &&
          loggedInUserRoles.some((item) => item.UserRoleCode === UserRoles.PricingApprover)
        )
      ) {
        if (loggedInEmployee) navigate("/home");
        else {
          dispatch(clearUser());
          navigate("/");
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loggedInEmployee]);

    return loggedInEmployee && <Component />;
  };
}

export function withContractAuthManagerRoleRequired(Component) {
  return function ProtectedUserRoleRoute() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const loggedInEmployee = useSelector(selectEmployee);
    const loggedInUserRoles = useSelector(selectUserRoles);
    useEffect(() => {
      if (
        !(
          loggedInEmployee &&
          loggedInUserRoles &&
          loggedInUserRoles.length &&
          loggedInUserRoles.some((item) => item.UserRoleCode === UserRoles.ContractAuthorityManager)
        )
      ) {
        if (loggedInEmployee) navigate("/home");
        else {
          dispatch(clearUser());
          navigate("/");
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loggedInEmployee]);

    return loggedInEmployee && <Component />;
  };
}

export function withEitherUserRoleOrPricingApprover(Component) {
  return function ProtectedUserRoleRoute() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const loggedInEmployee = useSelector(selectEmployee);
    const loggedInUserRoles = useSelector(selectUserRoles);
    useEffect(() => {
      if (
        !(
          loggedInEmployee &&
          loggedInUserRoles &&
          loggedInUserRoles.length &&
          loggedInUserRoles.some(
            (item) =>
              item.UserRoleCode === UserRoles.PricingApprover ||
              loggedInUserRoles.some((item) => item.UserRoleCode === UserRoles.PricingAdmin)
          )
        )
      ) {
        if (loggedInEmployee) navigate("/home");
        else {
          dispatch(clearUser());
          navigate("/");
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loggedInEmployee]);

    return loggedInEmployee && <Component />;
  };
}

export function HasUserRoles() {
  //1002 user role code is for pricing admin
  //1003 user role code is for pricing approver
  const loggedInUserRoles = useSelector(selectUserRoles);
  if (
    loggedInUserRoles &&
    loggedInUserRoles.length &&
    loggedInUserRoles.some((item) => item.UserRoleCode === UserRoles.PricingAdmin || item.UserRoleCode === UserRoles.Viewer)
  ) {
    return true;
  }
  return false;
}

export function IsLoggedInUserViewer() {
  const loggedInUserRoles = useSelector(selectUserRoles);
  //1004 user role code is for viewer
  if (
    loggedInUserRoles &&
    loggedInUserRoles.length &&
    loggedInUserRoles.some((item) => item.UserRoleCode === UserRoles.Viewer) &&
    loggedInUserRoles.every(
      (item) => item.UserRoleCode !== UserRoles.PricingAdmin && item.UserRoleCode !== UserRoles.PricingApprover
    )
  ) {
    return true;
  }
  return false;
}

export function IsLoggedInUserSuperAdmin() {
  const loggedInUserRoles = useSelector(selectUserRoles);
  //1001 user role code is for super admin
  if (
    loggedInUserRoles &&
    loggedInUserRoles.length &&
    loggedInUserRoles.some((item) => item.UserRoleCode === UserRoles.SuperAdmin)
  ) {
    return true;
  }
  return false;
}

export function IsLoggedInUserContractAuthManager() {
  const loggedInUserRoles = useSelector(selectUserRoles);
  //1001 user role code is for super admin
  if (
    loggedInUserRoles &&
    loggedInUserRoles.length &&
    loggedInUserRoles.some((item) => item.UserRoleCode === UserRoles.ContractAuthorityManager)
  ) {
    return true;
  }
  return false;
}

export function IsLoggedInUserPricingAdmin() {
  const loggedInUserRoles = useSelector(selectUserRoles);
  if (
    loggedInUserRoles &&
    loggedInUserRoles.length &&
    loggedInUserRoles.some((item) => item.UserRoleCode === UserRoles.PricingAdmin)
  ) {
    return true;
  }
  return false;
}

export function IsLoggedInUserPricingApprover() {
  const loggedInUserRoles = useSelector(selectUserRoles);
  if (
    loggedInUserRoles &&
    loggedInUserRoles.length &&
    loggedInUserRoles.some((item) => item.UserRoleCode === UserRoles.PricingApprover)
  ) {
    return true;
  }
  return false;
}
